import { useState, useEffect, useMemo } from 'react';
import supabase from '../supabaseClient';
import { sbSignedUrl } from '../services/supabase-api';
import { Inp } from './ui/FormControls';
import { exportarExcel, nombreArchivoExcel } from '../services/export-excel';

const formatFechaISO = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES") : "—";
const formatFechaHora = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const FILTROS_TIPO = ["Todos", "Entrada", "Salida", "Fotos", "Factura"];
const ICONO_TIPO = { Entrada: "🚛", Salida: "📦", Fotos: "📸", Factura: "🧾" };
const TIPO_DESTINO_LABEL = { 1: "Tipo 1 (Japón)", 2: "Tipo 2 (China)", 3: "Tipo 3 (GCC)", 0: "Otro" };

const abrirDocumento = async (url) => {
  if (!url) return;
  const signed = await sbSignedUrl(url);
  window.open(signed || url, "_blank");
};

// ── OPERARIO (INTERNACIONAL): ARCHIVO DOCUMENTAL ──────────────────
export const ArchivoInternacionalSection = ({ currentUser }) => {
  const almacen_id = currentUser?.almacen_id;

  const [movimientos, setMovimientos] = useState([]);
  const [registrosSalida, setRegistrosSalida] = useState([]);
  const [registrosFotos, setRegistrosFotos] = useState([]);
  const [registrosFactura, setRegistrosFactura] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [query, setQuery] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("Todos");
  const [njDetalle, setNjDetalle] = useState(null);

  useEffect(() => {
    if (!almacen_id) return;
    setCargando(true);
    Promise.all([
      supabase.from('movimientos_stock').select('*').eq('almacen_id', almacen_id).eq('tipo', 'entrada').not('albaran_url', 'is', null).order('creado_en', { ascending: false }),
      supabase.from('registros').select('*').eq('almacen_id', almacen_id).eq('tipo', 'Salida Contenedor').order('created_at', { ascending: false }),
      supabase.from('registros').select('*').eq('almacen_id', almacen_id).eq('tipo', 'Fotos Contenedor').order('created_at', { ascending: false }),
      supabase.from('registros').select('*').eq('almacen_id', almacen_id).eq('tipo', 'Factura').order('created_at', { ascending: false }),
      supabase.from('pedidos').select('*').eq('almacen_id', almacen_id),
    ]).then(([m, s, f, fa, p]) => {
      setMovimientos(m.data || []);
      setRegistrosSalida(s.data || []);
      setRegistrosFotos(f.data || []);
      setRegistrosFactura(fa.data || []);
      setPedidos(p.data || []);
    }).finally(() => setCargando(false));
  }, [almacen_id]);

  const entradasDedup = useMemo(() => {
    const seen = new Map();
    for (const m of movimientos) {
      if (!seen.has(m.numero_albaran)) seen.set(m.numero_albaran, m);
    }
    return [...seen.values()];
  }, [movimientos]);

  const porNj = useMemo(() => {
    const map = new Map();
    for (const r of registrosSalida) {
      const nj = r.datos?.nj;
      if (!nj) continue;
      if (!map.has(nj)) map.set(nj, {});
      map.get(nj).salida = r;
    }
    for (const r of registrosFotos) {
      const nj = r.datos?.nj;
      if (!nj) continue;
      if (!map.has(nj)) map.set(nj, {});
      map.get(nj).fotos = r;
    }
    for (const p of pedidos) {
      if (!p.nj) continue;
      if (!map.has(p.nj)) map.set(p.nj, {});
      map.get(p.nj).pedido = p;
    }
    return map;
  }, [registrosSalida, registrosFotos, pedidos]);

  const items = useMemo(() => {
    const list = [];
    for (const m of entradasDedup) {
      list.push({
        id: `entrada-${m.numero_albaran}-${m.id}`,
        tipoItem: "Entrada",
        identificador: m.numero_albaran,
        subtitulo: m.proveedor || "—",
        fecha: m.creado_en,
        doc: m.albaran_url,
      });
    }
    for (const r of registrosSalida) {
      const nj = r.datos?.nj;
      const completa = !!(nj && porNj.get(nj)?.salida && porNj.get(nj)?.fotos);
      list.push({
        id: `salida-${r.id}`,
        tipoItem: "Salida",
        identificador: nj || "—",
        subtitulo: `${r.datos?.contenedor || "—"} · ${r.datos?.destino || "—"}`,
        fecha: r.created_at,
        doc: r.datos?.fotoAlbaran,
        nj,
        completa,
      });
    }
    for (const r of registrosFotos) {
      const nj = r.datos?.nj;
      const completa = !!(nj && porNj.get(nj)?.salida && porNj.get(nj)?.fotos);
      list.push({
        id: `fotos-${r.id}`,
        tipoItem: "Fotos",
        identificador: nj || "—",
        subtitulo: `${(r.datos?.fotos || []).length} foto(s)`,
        fecha: r.created_at,
        nj,
        completa,
      });
    }
    for (const r of registrosFactura) {
      list.push({
        id: `factura-${r.id}`,
        tipoItem: "Factura",
        identificador: r.datos?.numero || "—",
        subtitulo: `Periodo ${r.datos?.periodo || "—"} · ${(Number(r.datos?.total) || 0).toFixed(2)} €`,
        fecha: r.created_at,
        doc: r.datos?.pdfUrl,
      });
    }
    return list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [entradasDedup, registrosSalida, registrosFotos, registrosFactura, porNj]);

  const filtrados = items.filter(it => {
    if (filtroTipo !== "Todos" && it.tipoItem !== filtroTipo) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (it.identificador || "").toLowerCase().includes(q) || (it.subtitulo || "").toLowerCase().includes(q);
  });

  const exportarArchivo = () => {
    const filas = filtrados.map(it => ({
      "Tipo": it.tipoItem,
      "Identificador": it.identificador,
      "Datos clave": it.subtitulo,
      "Fecha": formatFechaISO(it.fecha),
    }));
    exportarExcel(nombreArchivoExcel("archivo", currentUser?.almacen_nombre), [{ nombre: "Archivo", filas }]);
  };

  const abrirItem = (it) => {
    if (it.nj) { setNjDetalle(it.nj); return; }
    abrirDocumento(it.doc);
  };

  const detalle = njDetalle ? porNj.get(njDetalle) : null;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <div className="flex items-center justify-between border-b pb-2">
          <h2 className="font-bold text-gray-800">🗂️ Archivo</h2>
          <button onClick={exportarArchivo} disabled={filtrados.length === 0} className="bg-white text-gray-700 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-all">⬇️ Exportar Excel</button>
        </div>
        <Inp value={query} onChange={setQuery} placeholder="Buscar por nº albarán, NJ o proveedor..." />
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTROS_TIPO.map(t => (
            <button
              key={t}
              onClick={() => setFiltroTipo(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap border ${filtroTipo === t ? "bg-slate-700 text-white border-slate-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {cargando && <p className="text-sm text-gray-400 text-center py-4">Cargando...</p>}
        {!cargando && filtrados.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">No hay documentos que coincidan.</p>
        )}
        {!cargando && filtrados.map(it => (
          <div key={it.id} onClick={() => abrirItem(it)} className="bg-white rounded-xl shadow p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border border-gray-100">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-lg flex-shrink-0">{ICONO_TIPO[it.tipoItem]}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-800 text-sm truncate">{it.tipoItem} · {it.identificador}</p>
                {it.completa && <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex-shrink-0">🔗 Carga completa</span>}
              </div>
              <p className="text-xs text-gray-500 truncate">{it.subtitulo}</p>
            </div>
            <p className="text-xs text-gray-400 flex-shrink-0">{formatFechaISO(it.fecha)}</p>
          </div>
        ))}
      </div>

      {njDetalle && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-4 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-2">
              <h2 className="font-bold text-gray-800">🔗 {njDetalle}</h2>
              <button onClick={() => setNjDetalle(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>

            {detalle?.pedido && (
              <div className="border rounded-lg p-3">
                <p className="text-xs font-bold text-gray-500 uppercase mb-1">Pedido</p>
                <p className="text-sm text-gray-700">{detalle.pedido.numero_pedido} — {detalle.pedido.cliente || "sin cliente"}</p>
                <p className="text-xs text-gray-500">Estado: {detalle.pedido.estado}</p>
              </div>
            )}

            {detalle?.salida && (
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-xs font-bold text-gray-500 uppercase">Salida contenedor</p>
                <p className="text-sm text-gray-700">Contenedor: {detalle.salida.datos?.contenedor || "—"} · Precinto: {detalle.salida.datos?.precinto || "—"}</p>
                <p className="text-sm text-gray-700">Destino: {detalle.salida.datos?.destino || "—"} · Tipo: {TIPO_DESTINO_LABEL[Number(detalle.salida.datos?.tipoDestino)] ?? "—"}</p>
                <p className="text-xs text-gray-500">Manta: {detalle.salida.datos?.manta ? "Sí" : "No"} · Bolsas: {detalle.salida.datos?.bolsas ? "Sí" : "No"} · Manipulado: {detalle.salida.datos?.manipulado ? "Sí" : "No"}</p>
                <p className="text-xs text-gray-500">{detalle.salida.fecha}</p>
                {detalle.salida.datos?.checklist && (
                  <div className="space-y-0.5">
                    {Object.entries(detalle.salida.datos.checklist).map(([k, v]) => (
                      <p key={k} className="text-xs text-gray-600">{k}: <span className="font-semibold">{v || "—"}</span></p>
                    ))}
                  </div>
                )}
                {detalle.salida.datos?.fotoAlbaran && (
                  <button onClick={() => abrirDocumento(detalle.salida.datos.fotoAlbaran)} className="w-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold px-3 py-2 rounded-lg hover:bg-blue-100">
                    📥 Ver albarán de salida
                  </button>
                )}
              </div>
            )}

            {detalle?.fotos && (
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-xs font-bold text-gray-500 uppercase">Fotos ({(detalle.fotos.datos?.fotos || []).length})</p>
                <div className="grid grid-cols-3 gap-2">
                  {(detalle.fotos.datos?.fotos || []).map((f, i) => (
                    <a
                      key={i}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block border rounded-lg overflow-hidden"
                      onClick={async (e) => { e.preventDefault(); const url = await sbSignedUrl(f.url); window.open(url || f.url, "_blank"); }}
                    >
                      <img
                        src={f.url}
                        alt={`foto ${i + 1}`}
                        className="w-full h-20 object-cover"
                        onError={async (e) => { const url = await sbSignedUrl(f.url); if (url) e.target.src = url; }}
                      />
                      <p className="text-[9px] text-gray-500 text-center py-0.5">{formatFechaHora(f.subidoEn)}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {!detalle?.pedido && !detalle?.salida && !detalle?.fotos && (
              <p className="text-sm text-gray-500">Sin datos adicionales para este NJ.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
